// @greenlight-spec: 11-observability.md#per-app-reservation-attribution
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Link } from 'react-router-dom';

import { SectionCard } from '@/components/greenlight/SectionCard';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { reservationGap, type AppReservation } from '@greenlight/shared';

const numberFmt = new Intl.NumberFormat('en-US');

function unusedText(reserved: number | null, used: number | null, unit: string): string {
  const unused = reservationGap(reserved, used);
  if (unused === null) return 'not measured';
  return `${numberFmt.format(Math.round(unused))}${unit} unused of ${numberFmt.format(Math.round(reserved ?? 0))}${unit}`;
}

/**
 * Per-app attribution for the aggregate reservation the capacity card reports:
 * which apps hold app-node capacity they do not spend, largest first. The
 * aggregate says a pool is full; this says who filled it, so finding the
 * responsible apps does not mean reading every manifest.
 */
export function AppReservationsSection({ pollMs }: { pollMs?: number }): React.ReactElement {
  const { data, isLoading, isError } = api.health.appReservations.useQuery(
    ['health-app-reservations'],
    undefined,
    { queryKey: ['health-app-reservations'], refetchInterval: pollMs },
  );

  const body = data?.status === 200 ? data.body : undefined;

  return (
    <SectionCard title="Reserved but unused by app" className="md:col-span-2">
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : isError || !body ? (
        <p className="text-sm text-muted-foreground">Couldn&apos;t load per-app reservations.</p>
      ) : body.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No running app has both a reservation and a usage sample to compare it against yet.
        </p>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            What each app reserves on the cluster's nodes and what it is measured using. Requests
            are held whether or not the app spends them; a declared <code>compute:</code> block sets
            them to the limit, so an over-sized one holds the most.
          </p>
          <ul className="space-y-2">
            {body.items.map((row) => (
              <ReservationRow key={row.app_id} row={row} />
            ))}
          </ul>
          {body.ranked_count > body.items.length && (
            <p className="text-xs text-muted-foreground">
              Showing the {body.items.length} largest of {body.ranked_count} ranked apps.
            </p>
          )}
          {body.unmeasured_count > 0 && (
            <p className="text-xs text-muted-foreground">
              {body.unmeasured_count} further app{body.unmeasured_count === 1 ? '' : 's'} could not
              be compared: no recent sample reported a reservation with its usage.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function ReservationRow({ row }: { row: AppReservation }): React.ReactElement {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border pb-2 last:border-b-0 last:pb-0">
      <Link to={`/apps/${row.app_id}`} className="font-medium text-foreground hover:underline">
        {row.name}
      </Link>
      <span className="font-mono text-[11px] text-muted-foreground">
        CPU {unusedText(row.cpu_reserved_m, row.cpu_used_m, 'm')} · memory{' '}
        {unusedText(row.memory_reserved_mb, row.memory_used_mb, ' MiB')}
      </span>
    </li>
  );
}
